// @givmo/board — a disclaimer class rendered from the registry, never from this repo.
//
// ══ WHY THE CLIENT MUST NOT AUTHOR THIS SENTENCE ═══════════════════════════════════════════════
//
// The disclaimer registry (ST2) is the single versioned source for the module's legal-shaped copy:
// each class is a document with an effective date, a version number and a content hash, put in force
// by a signed set-effective act and served by `GET /disclaimers/resolve`. A sentence typed into a
// React file is none of those things — it has no version, no effective date, cannot be corrected
// without a deploy, and nothing anywhere records which words an organization was actually shown on
// a given day.
//
// So this component renders `content_plaintext` VERBATIM and writes nothing of its own. The board
// conflict surfaces take `BOARD_SELF_REPORTED` — the registry's `self-reported-unverified` class,
// named without that word because §3.4 bans the root down to field names.
//
// ⚠ **AN UNRESOLVED CLASS RENDERS NOTHING, AND THAT IS THE DESIGNED BEHAVIOUR.** Not a fallback
// sentence, not a placeholder, not "loading…". The two ways a client can be wrong here are opposite
// and only one is recoverable: showing the registry's words late is a cosmetic delay, while showing
// words the registry never published is this repo asserting a legal-shaped statement on the organization's
// screen with no version behind it and no record of it. `@givmo/disclosure` takes the same posture for
// a surface with no compiled floor — a floor-less null renders nothing — and the board module has no
// floors at all, so the null case is every case here until the document is published to an
// environment. A surface whose disclaimer is absent looks unfinished, which is the honest appearance
// of a surface whose disclaimer has not been published yet.
//
// This deliberately does NOT reach for `@givmo/disclosure`. That package carries ETag/304 caching,
// per-surface iOS-parity floors, the `409 DISCLOSURE_STALE` detector and the donation echo — machinery
// for the money surfaces, all of which the board module would take as a dependency to use none of. The
// generated `disclaimersResolveDisclaimer` operation is already in the committed snapshot and already
// on this app's api-client, so the read costs nothing new.

import { useQuery } from '@tanstack/react-query';
import { sdk, type DisclaimersResolveDisclaimerResponse } from '@givmo/api-client';
import { testIdProp } from '@givmo/design-system';
import { useBoardDisclaimerTarget } from '../board-runtime';

/**
 * The registry surfaces this app renders.
 *
 * A closed union rather than a string: a typo in a surface key resolves to nothing at all, and
 * "renders nothing" is indistinguishable from the legitimate not-yet-published case — so the typo
 * would be invisible in every environment until someone noticed a missing paragraph.
 */
export type RegisteredDisclaimerSurface =
  | 'BOARD_SELF_REPORTED'
  // CBG-YR5-VIEW: the Form 990 page's foot (contract §7), the read's `evidence-not-conclusion` and
  // `not-your-lawyer`. In force since CBG-ST2-EFFECTIVE (D-CBG-60, D-CBG-62), each at its version 2,
  // as is `BOARD_SELF_REPORTED`; an environment that has not run that migration resolves nothing.
  | 'BOARD_EVIDENCE_NOT_CONCLUSION'
  | 'BOARD_NOT_YOUR_LAWYER';

/**
 * How each surface is drawn. Board's disclaimers are fine print (Jonathan's standing ruling keeps
 * them small), all but one: the counsel sentence is drawn at Compliance's size — 13 px, the darker
 * grey, no box, on its own line — wherever it shows (D-CBG-62). Texas excludes a product from the
 * practice of law only when it states "clearly and conspicuously" that it is not a substitute for
 * an attorney's advice (Tex. Gov't Code § 81.101(c)), and Compliance draws its own sentence so.
 */
const PRESENTATION: Readonly<Record<RegisteredDisclaimerSurface, 'fine-print' | 'counsel'>> = {
  BOARD_SELF_REPORTED: 'fine-print',
  BOARD_EVIDENCE_NOT_CONCLUSION: 'fine-print',
  BOARD_NOT_YOUR_LAWYER: 'counsel',
};

const CLASS_NAMES = {
  'fine-print':
    'whitespace-pre-wrap rounded-md border border-border-subtle bg-surface-sunken p-12 font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary',
  counsel:
    'whitespace-pre-wrap text-pretty font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary',
} as const;

export function registeredDisclaimerKey(
  surface: RegisteredDisclaimerSurface,
  orgRef: string | null,
): readonly unknown[] {
  return ['board-registered-disclaimer', surface, orgRef];
}

export interface RegisteredDisclaimerProps {
  surface: RegisteredDisclaimerSurface;
  /**
   * Scopes the resolve to the organization, so a jurisdiction-specific version can be served. The
   * host says how its class addresses the registry (`BoardRuntime.disclaimerTarget`); the module
   * never spells that key itself.
   */
  orgRef: string | null;
  testId?: string;
}

/**
 * Read on the host's configured client, by an officer or a director alike: a director is the
 * console's own signed-in account (CBG-SEC2, D-CBG-16), so there is one credential and one answer.
 */
export function RegisteredDisclaimer({ surface, orgRef, testId }: RegisteredDisclaimerProps) {
  const target = useBoardDisclaimerTarget(orgRef);
  const query = useQuery({
    queryKey: registeredDisclaimerKey(surface, orgRef),
    // A 404 is the ORDINARY answer for a class not yet published to this environment, not a fault —
    // retrying it just delays rendering nothing. Any other failure lands in the same place, because
    // the disposition is identical: this component has nothing it is allowed to say.
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<DisclaimersResolveDisclaimerResponse> => {
      const { data } = await sdk.disclaimersResolveDisclaimer({
        query: { surface, ...target },
        throwOnError: true,
      });
      return data;
    },
  });

  const copy =
    typeof query.data?.content_plaintext === 'string' ? query.data.content_plaintext : '';
  if (copy.trim().length === 0) return null;

  return (
    <p
      // `whitespace-pre-wrap`: a registry document is authored as paragraphs, and collapsing them
      // would render a version of the text that is not the version whose hash was published.
      className={CLASS_NAMES[PRESENTATION[surface]]}
      {...testIdProp(testId)}
    >
      {copy}
    </p>
  );
}

export interface CounselSentenceProps {
  orgRef: string | null;
  testId?: string;
}

/**
 * Board's counsel sentence beside the forms (D-CBG-62): on its own line under an opened form's
 * review line, and once under each list of forms, never on every card. The registry's words, drawn
 * like every other placement of the class; nothing at all until the class is in force.
 */
export function CounselSentence({ orgRef, testId }: CounselSentenceProps) {
  return <RegisteredDisclaimer surface="BOARD_NOT_YOUR_LAWYER" orgRef={orgRef} testId={testId} />;
}
